import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link2, ArrowLeft, Loader2, Calendar, Hash, ExternalLink, Check, X, AlertTriangle } from 'lucide-react';
import { DashboardLayout } from '@/components/layout';
import { Card, CardTitle, Button, Input, PlatformIcon } from '@/components/ui';
import { useToast } from '@/components/ui';
import { getDeeplinkById, validateCode, isCodeAvailable } from '@/services/deeplinks';
import { useDeeplinks } from '@/hooks/useDeeplinks';
import { getPlatformConfig } from '@/utils/platformDetector';
import { getDomain } from '@/utils/domainResolver';
import type { Deeplink } from '@/types';

const DeeplinkEdit = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { update } = useDeeplinks();
  const { addToast } = useToast();

  const [deeplink, setDeeplink] = useState<Deeplink | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [originalCode, setOriginalCode] = useState('');

  // Code validation state
  const [codeStatus, setCodeStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable' | 'invalid' | 'reserved'>('idle');
  const [codeError, setCodeError] = useState<string | null>(null);

  // Debounced code validation
  const checkCodeAvailability = useCallback(async (newCode: string, currentId: string, currentCode: string) => {
    // If code hasn't changed, reset to idle
    if (newCode === currentCode) {
      setCodeStatus('idle');
      setCodeError(null);
      return;
    }

    // Validate format first
    const validation = validateCode(newCode);
    if (!validation.valid) {
      if (validation.error === 'reserved') {
        setCodeStatus('reserved');
        setCodeError(t('editDeeplink.codeReserved'));
      } else if (validation.error === 'too_short') {
        setCodeStatus('invalid');
        setCodeError(t('editDeeplink.codeTooShort'));
      } else if (validation.error === 'too_long') {
        setCodeStatus('invalid');
        setCodeError(t('editDeeplink.codeTooLong'));
      } else if (validation.error === 'starts_with_at') {
        setCodeStatus('invalid');
        setCodeError(t('editDeeplink.codeStartsWithAt'));
      } else {
        setCodeStatus('invalid');
        setCodeError(t('editDeeplink.codeInvalid'));
      }
      return;
    }

    // Check availability
    setCodeStatus('checking');
    try {
      const available = await isCodeAvailable(newCode, currentId);
      if (available) {
        setCodeStatus('available');
        setCodeError(null);
      } else {
        setCodeStatus('unavailable');
        setCodeError(t('editDeeplink.codeUnavailable'));
      }
    } catch {
      setCodeStatus('invalid');
      setCodeError(t('common.somethingWentWrong'));
    }
  }, [t]);

  // Debounce code checking
  useEffect(() => {
    if (!id || !originalCode) return;

    const timeoutId = setTimeout(() => {
      if (code.length > 0) {
        checkCodeAvailability(code, id, originalCode);
      } else {
        setCodeStatus('idle');
        setCodeError(null);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [code, id, originalCode, checkCodeAvailability]);

  // Load deeplink data
  useEffect(() => {
    const loadDeeplink = async () => {
      if (!id) {
        setError(t('editDeeplink.notFound'));
        setLoading(false);
        return;
      }

      try {
        const data = await getDeeplinkById(id);
        if (!data) {
          setError(t('editDeeplink.notFound'));
          setLoading(false);
          return;
        }

        setDeeplink(data);
        setName(data.name || '');
        setCode(data.code);
        setOriginalCode(data.code);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('common.somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    };

    loadDeeplink();
  }, [id, t]);

  const handleSave = async () => {
    if (!deeplink || !id) return;

    // Prevent saving if code is invalid or unavailable
    if (code !== originalCode && (codeStatus === 'invalid' || codeStatus === 'unavailable' || codeStatus === 'reserved' || codeStatus === 'checking')) {
      addToast({
        type: 'error',
        title: t('editDeeplink.validationError'),
        message: codeError || t('editDeeplink.codeInvalid'),
      });
      return;
    }

    setSaving(true);

    try {
      const updateData: { name?: string; code?: string } = {
        name: name.trim() || undefined,
      };

      // Only include code if it changed
      if (code !== originalCode) {
        updateData.code = code;
      }

      await update(id, updateData);

      addToast({
        type: 'success',
        title: t('editDeeplink.saved'),
        message: t('editDeeplink.savedMessage'),
      });

      navigate('/deeplinks');
    } catch (err) {
      addToast({
        type: 'error',
        title: t('editDeeplink.saveFailed'),
        message: err instanceof Error ? err.message : t('common.somethingWentWrong'),
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp: { seconds: number } | Date | null) => {
    if (!timestamp) return t('common.unknown');
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString(i18n.language, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !deeplink) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Link2 className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-dark-800 dark:text-white mb-2">{t('editDeeplink.notFound')}</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{t('editDeeplink.notFoundMessage')}</p>
          <Button variant="primary" onClick={() => navigate('/deeplinks')}>
            {t('editDeeplink.backToDeeplinks')}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/deeplinks')}
          className="flex items-center gap-2 text-gray-500 hover:text-dark-800 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="dark:text-gray-400">{t('editDeeplink.backToDeeplinks')}</span>
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">{t('editDeeplink.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400">{t('editDeeplink.subtitle')}</p>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* Deeplink Info (Read-only) */}
        <Card variant="default" padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-lavender-100 rounded-xl flex items-center justify-center">
              <Hash className="w-5 h-5 text-lavender-700" />
            </div>
            <CardTitle>{t('editDeeplink.deeplinkInfo')}</CardTitle>
          </div>

          <div className="space-y-4">
            {/* Short URL - Now editable */}
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {t('editDeeplink.customCode')}
              </label>
              <div className="flex items-center">
                <span className="px-4 py-3 bg-gray-100 dark:bg-dark-700 rounded-l-xl border border-r-0 border-gray-200 dark:border-dark-600 text-gray-500 dark:text-gray-400 font-medium">
                  {deeplink ? getDomain(deeplink) : 'klipr.it'}/
                </span>
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                    className={`w-full px-4 py-3 border rounded-r-xl focus:outline-none focus:ring-2 transition-colors dark:bg-dark-700 dark:text-white ${
                      codeStatus === 'available' ? 'border-green-500 focus:ring-green-500/20' :
                      codeStatus === 'unavailable' || codeStatus === 'invalid' || codeStatus === 'reserved' ? 'border-red-500 focus:ring-red-500/20' :
                      'border-gray-200 dark:border-dark-600 focus:ring-lime-500/20 focus:border-lime-500'
                    }`}
                    placeholder="my-custom-code"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {codeStatus === 'checking' && (
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    )}
                    {codeStatus === 'available' && (
                      <Check className="w-5 h-5 text-green-500" />
                    )}
                    {codeStatus === 'unavailable' && (
                      <X className="w-5 h-5 text-red-500" />
                    )}
                    {codeStatus === 'invalid' && (
                      <X className="w-5 h-5 text-red-500" />
                    )}
                    {codeStatus === 'reserved' && (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                </div>
              </div>
              {codeError && (
                <p className={`mt-2 text-sm ${codeStatus === 'reserved' ? 'text-amber-600' : 'text-red-600'}`}>
                  {codeError}
                </p>
              )}
              {codeStatus === 'available' && code !== originalCode && (
                <p className="mt-2 text-sm text-green-600">
                  {t('editDeeplink.codeAvailable')}
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('editDeeplink.codeHint')}
              </p>
            </div>

            {/* Destination URL */}
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {t('editDeeplink.destinationUrl')}
              </label>
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-dark-700 rounded-xl border border-gray-200 dark:border-dark-600">
                <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-dark-800 dark:text-gray-200 truncate">{deeplink.originalUrl}</span>
              </div>
            </div>

            {/* Platform */}
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {t('editDeeplink.platform')}
              </label>
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-dark-700 rounded-xl border border-gray-200 dark:border-dark-600">
                <PlatformIcon platform={deeplink.platform} size="sm" />
                <span className="text-dark-800 dark:text-gray-200">{getPlatformConfig(deeplink.platform).name}</span>
              </div>
            </div>

            {/* Created At */}
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {t('editDeeplink.createdAt')}
              </label>
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-dark-700 rounded-xl border border-gray-200 dark:border-dark-600">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-dark-800 dark:text-gray-200">{formatDate(deeplink.createdAt)}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Edit Form */}
        <Card variant="default" padding="lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-lime-100 rounded-xl flex items-center justify-center">
              <Link2 className="w-5 h-5 text-lime-700" />
            </div>
            <CardTitle>{t('editDeeplink.editDetails')}</CardTitle>
          </div>

          <div className="space-y-4">
            {/* Name */}
            <Input
              label={t('createModal.nameLabel')}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('createModal.namePlaceholder')}
              hint={t('createModal.nameHint')}
            />

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/deeplinks')}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                isLoading={saving}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DeeplinkEdit;
